import { useRef } from 'react';

import { gql, useQuery } from '@apollo/client';

import { TRACE_LINKS_ANCHOR_HALF_WINDOW } from '@/constant/pagination';

export const TRACE_LINK_HEIGHT_QUERY = gql`
  query traceLinkHeightQuery($traceId: UUID!, $linkHash: String!) {
    traceById(id: $traceId) {
      rowId
      head {
        linkHash
        height
      }
      links(filter: { linkHash: { equalTo: $linkHash } }) {
        nodes {
          linkHash
          height
        }
      }
    }
  }
`;

export type TraceLinkAnchor = {
  offset: number;
  resolving: boolean;
};

type FrozenAnchor = {
  traceId: string | undefined;
  offset: number;
};

/**
 * Resolves the initial links-window offset for the trace inspector.
 *
 * The route carries the target link's hash, not its height, so a deep link
 * first fetches `{ head.height, target.height }` and derives the offset that
 * centers the target: links are height DESC, so a link at height H sits at
 * offset (headHeight - H), backed off by half a window.
 */
export function useTraceLinkAnchor(
  traceId: string | undefined,
  linkid: string | undefined
): TraceLinkAnchor {
  // Frozen per TRACE, not per linkid: in-trace navigation must never
  // re-resolve, or the whole inspector unmounts behind `resolving`.
  const frozen = useRef<FrozenAnchor>({ traceId: undefined, offset: 0 });
  const shouldResolve = !!linkid && frozen.current.traceId !== traceId;

  const { data, loading } = useQuery(TRACE_LINK_HEIGHT_QUERY, {
    variables: { traceId, linkHash: linkid },
    skip: !shouldResolve,
    fetchPolicy: 'cache-first'
  });

  if (!linkid) {
    // Opening on /trace/:id IS the offset-0 anchor. Claim the trace here, or
    // the head-link URL setInitialUrl replaces in next reads as a cold deep
    // link, re-resolves, and unmounts the already-loaded inspector.
    frozen.current = { traceId, offset: 0 };
    return { offset: 0, resolving: false };
  }

  if (shouldResolve) {
    const headHeight = data?.traceById?.head?.height;
    const targetHeight = data?.traceById?.links?.nodes?.[0]?.height;
    if (headHeight != null && targetHeight != null) {
      const rawOffset =
        headHeight - targetHeight - TRACE_LINKS_ANCHOR_HALF_WINDOW;
      frozen.current = { traceId, offset: Math.max(0, rawOffset) };
    }
  }

  return {
    offset: frozen.current.offset,
    resolving: loading && frozen.current.traceId !== traceId
  };
}
